import React from "react";
import firebase from "firebase";
import GeneralApi from "../../app/generalApi";
const generalApi = new GeneralApi();
import { Alert, Button, Form, Input, Modal, message } from "antd";
import { accessError, getAccountAccess, saveAccountAccess, reauthenticationProvider } from "../../app/accountAccess";

export default function AccountAccess({ automatic = false, routePath }) {
  const [visible, setVisible] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [username, setUsername] = React.useState("");
  const [federated, setFederated] = React.useState(false);
  const [error, setError] = React.useState("");
  const [form] = Form.useForm();

  async function open() {
    setLoading(true);
    setError("");
    try {
      const data = await getAccountAccess();
      const session = await firebase.auth().currentUser.getIdTokenResult();
      setUsername(data.username || "");
      setFederated(!!reauthenticationProvider(firebase.auth().currentUser, session));
      form.resetFields();
      form.setFieldsValue({ username: data.username || "" });
      setVisible(true);
    } catch (e) { message.error(accessError(e)); }
    finally { setLoading(false); }
  }

  React.useEffect(() => {
    if (!automatic) return undefined;
    let active = true;
    let generation = 0;
    const unsubscribe = firebase.auth().onAuthStateChanged(async user => {
      const version = ++generation;
      setVisible(false);
      if (!user || user.isAnonymous) return;
      try {
        // API v2 owns the Firestore profile and quota. Never read the legacy RTDB account.
        const account = await generalApi.getUserInfo(user.uid);
        if (!active || generation !== version || !account || account.disabled || Number(account.conexiones) < 7) return;
        if (!(Number(account.conexiones) >= 7) || sessionStorage.getItem("access-prompt:" + user.uid)) return;
        const access = await getAccountAccess();
        if (!active || generation !== version || access.username) return;
        const session = await user.getIdTokenResult();
        if (!active || generation !== version || sessionStorage.getItem("access-prompt:" + user.uid)) return;
        sessionStorage.setItem("access-prompt:" + user.uid, "shown");
        setUsername("");
        setFederated(!!reauthenticationProvider(user, session));
        form.resetFields();
        const billing = account.facturacion || {};
        const suggested = String(billing.es_cif || billing.esCif || "").trim().toLowerCase().replace(/[ -]/g, "");
        if (/^[a-z0-9][a-z0-9._-]{2,39}$/.test(suggested)) form.setFieldsValue({ username: suggested });
        setVisible(true);
      } catch (e) { /* Setup remains available from Mi suscripción after a failed request. */ }
    });
    return () => { active = false; generation++; unsubscribe(); };
  }, [automatic, routePath]);

  async function save(values) {
    setLoading(true);
    setError("");
    try {
      const result = await saveAccountAccess(values.username, values.password);
      setUsername(result.username);
      form.resetFields();
      setVisible(false);
      message.success("Acceso guardado. Ya puedes entrar con tu usuario y contraseña.");
    } catch (e) { setError(accessError(e)); }
    finally { setLoading(false); }
  }

  return <React.Fragment>
    {!automatic && <Button type="primary" block size="large" style={{ marginTop: 20 }} loading={loading} onClick={open}>
      Configurar usuario y contraseña
    </Button>}
    <Modal title="Tu usuario de acceso a Escuelapp" visible={visible} footer={null}
      onCancel={() => { if (!loading) { setVisible(false); form.resetFields(); } }} forceRender>
      <p>Elige un usuario para entrar sin usar Google ni compartir su contraseña. Si tienes la modalidad de oficina, tu equipo podrá usar este mismo acceso dentro del límite de conexiones de tu plan.</p>
      <p>Tu correo de facturación y tu suscripción se mantienen. La contraseña elegida servirá también para entrar con tu email en Escuelapp.</p>
      {error && <Alert type="error" showIcon message={error} style={{ marginBottom: 16 }} />}
      <Form form={form} layout="vertical" onFinish={save}>
        <Form.Item label="Usuario" name="username" rules={[{ required: true, message: "Elige un usuario." },
          { pattern: /^[a-zA-Z0-9][a-zA-Z0-9._-]{2,39}$/, message: "Entre 3 y 40 letras, números, puntos, guiones o guiones bajos." }]}>
          <Input autoComplete="username" maxLength={40} placeholder="Por ejemplo, oficina-jimeno" />
        </Form.Item>
        <Form.Item label={username ? "Nueva contraseña (opcional)" : "Contraseña"} name="password" rules={[
          { required: !username, message: "Elige una contraseña." },
          { min: 10, max: 128, message: "Usa entre 10 y 128 caracteres." }]}>
          <Input.Password autoComplete="new-password" />
        </Form.Item>
        <Form.Item label="Repite la nueva contraseña" name="confirmation" dependencies={["password"]} rules={[
          ({ getFieldValue }) => ({ validator(rule, value) {
            return (getFieldValue("password") || "") === (value || "") ? Promise.resolve() : Promise.reject("Las contraseñas no coinciden.");
          } })]}>
          <Input.Password autoComplete="new-password" />
        </Form.Item>
        {federated && <p>Si tu sesión es antigua, te pediremos que confirmes con Google o Apple. Si acabas de entrar, se guardará directamente.</p>}
        <Button type="primary" htmlType="submit" block loading={loading}>Guardar acceso</Button>
      </Form>
    </Modal>
  </React.Fragment>;
}



// WEBPACK FOOTER //
// ./components/AccountAccess/index.js